import React, { useEffect, useState, useRef } from 'react';
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical } from 'lucide-react';

const WhatsAppMsg = () => {
  const isLocal = window.location.hostname === 'localhost';
  const API_BASE_URL = isLocal ? 'https://crm-tkxn.onrender.com' : 'https://crm-tkxn.onrender.com';
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [showAlert, setShowAlert] = useState(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userRole, setUserRole] = useState('');
  const topRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    location: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role || 'user');
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchTemplates(selectedCompanyId);
    }
  }, [selectedCompanyId, userRole]);

  const fetchCompanies = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("No access token found. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);

      if (decoded.role === 'superadmin') {
        const response = await axios.get(
          `${API_BASE_URL}/api/companies`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCompanies(response.data || []);
        if (response.data.length > 0) {
          setSelectedCompanyId(response.data[0]._id);
        }
      } else if (decoded.role === 'company') {
        const companyId = decoded.companyId || decoded.id;
        const response = await axios.get(
          `${API_BASE_URL}/api/companies/${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCompanies([response.data]);
        setSelectedCompanyId(companyId);
      } else {
        const companyId = decoded.company || decoded.companyId;
        const employeeId = decoded.id || decoded._id;
        try {
          const profileResponse = await axios.get(
            `${API_BASE_URL}/api/employees/${employeeId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const companyData = profileResponse.data?.company;
          if (companyData && typeof companyData === 'object') {
            setCompanies([{ _id: companyId, businessName: companyData.businessName }]);
          } else {
            setCompanies([{ _id: companyId, businessName: 'My Company' }]);
          }
        } catch (err) {
          setCompanies([{ _id: companyId, businessName: 'My Company' }]);
        }
        setSelectedCompanyId(companyId);
      }
    } catch (err) {
      console.error("Invalid token:", err);
      setError("Invalid token. Please log in again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (companyId) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/whatsapp-msg/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(templates);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setTemplates(items);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    if (!formData.title.trim() || !formData.message.trim()) {
      setShowAlert("Please fill in all fields.");
      return;
    }

    if (!selectedCompanyId) {
      setShowAlert("Please select a company first.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/whatsapp-msg/company/${selectedCompanyId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        setShowSuccessAlert("Template created successfully!");
        fetchTemplates(selectedCompanyId);
        resetForm();
      }
    } catch (err) {
      setShowAlert(err.response?.data?.message || "Error creating template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTemplate = (template) => {
    setIsEditing(true);
    setEditingTemplateId(template._id);
    setFormData({
      title: template.title,
      message: template.message,
      location: template.location || ""
    });
    setShowForm(true);
    setScrollTrigger(prev => prev + 1);
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("authToken");

    setSubmitting(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/whatsapp-msg/${editingTemplateId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowSuccessAlert("Template updated successfully!");
      fetchTemplates(selectedCompanyId);
      resetForm();
    } catch (err) {
      setShowAlert(err.response?.data?.message || "Error updating template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = (id, title) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the template "${title}"?`,
      id: id
    });
  };

  const handleConfirmAction = async () => {
    const token = localStorage.getItem("authToken");
    try {
      setSubmitting(true);
      if (confirmAction.type === 'delete') {
        await axios.delete(
          `${API_BASE_URL}/api/whatsapp-msg/${confirmAction.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShowSuccessAlert('Template deleted successfully!');
        fetchTemplates(selectedCompanyId);
      }
    } catch (error) {
      setShowAlert(error.response?.data?.message || "Error deleting template");
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", message: "", location: "" });
    setIsEditing(false);
    setEditingTemplateId(null);
    setShowForm(false);
  };

  if (loading && !templates.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div ref={topRef} className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">WhatsApp Messages</h1>
            <p className="text-gray-600 mt-2">Manage your WhatsApp message templates</p>
          </div>

          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            {userRole === 'superadmin' && (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md"
              >
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>{company.businessName}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 transition-all shadow-md"
            >
              {showForm ? "Cancel" : "Add Template"}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <h2 className="text-xl font-semibold mb-6">{isEditing ? "Edit Template" : "Add New Template"}</h2>
            <form onSubmit={isEditing ? handleUpdateTemplate : handleAddTemplate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    placeholder="e.g., Welcome Message"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message Content</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    placeholder="Enter your message template here..."
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location (Optional)</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., Office, Branch A, or Coordinates"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    {submitting ? "Saving..." : (isEditing ? "Update Template" : "Save Template")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-800">Templates</h2>
            <span className="text-sm text-gray-600">{templates.length} templates found</span>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="templates">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="divide-y divide-gray-200"
                >
                  {templates.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No templates found.</div>
                  ) : (
                    templates.map((template, index) => (
                      <Draggable key={template._id} draggableId={template._id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`p-6 hover:bg-gray-50 transition-colors ${snapshot.isDragging ? 'bg-indigo-50 shadow-lg' : 'bg-white'}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex items-start flex-1">
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-1 mr-4 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100"
                                >
                                  <GripVertical size={20} />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-medium text-gray-900">{template.title}</h3>
                                  <p className="text-gray-600 mt-2 whitespace-pre-wrap">{template.message}</p>
                                  {template.location && (
                                    <div className="mt-2 flex items-center text-sm text-gray-500">
                                      <span className="font-medium mr-1">📍 Location:</span> {template.location}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex space-x-2 ml-4">
                                <button onClick={() => handleEditTemplate(template)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md">
                                  Edit
                                </button>
                                <button onClick={() => handleDeleteTemplate(template._id, template.title)} className="p-2 text-red-600 hover:bg-red-50 rounded-md">
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    )
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>

      {confirmAction && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4">Confirmation</h2>
            <p className="text-gray-700 mb-6">{confirmAction.message}</p>
            <div className="flex justify-center space-x-4">
              <button onClick={handleConfirmAction} className="px-4 py-2 bg-indigo-600 text-white rounded-md">Confirm</button>
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessAlert && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
            <h2 className="text-lg font-semibold mb-4 text-green-600">Success</h2>
            <p className="text-gray-700 mb-6">{showSuccessAlert}</p>
            <button onClick={() => setShowSuccessAlert(null)} className="px-4 py-2 bg-indigo-600 text-white rounded-md w-full">OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppMsg;
